import { Link, createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ClienteDetalhe, Departamento, Membro, RegimeTributario } from '@sgc/contracts';
import { rotuloAnexos } from '@sgc/contracts';
import { del, get, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { OPCOES_REGIME, ROTULO_REGIME, competenciaAtual, formatarCompetencia, formatarDocumento, lerCompetencia } from '../lib/formato';
import { Campo, Sel, CampoCompetencia } from '../ui/campos';
import { Modal, Pill, Sec, Tabs } from '../ui/layout';
import { useToast } from '../ui/toast';
import { CampoAnexos } from '../telas/clientes/anexos-simples';
import { FichaCliente, bodyDe, errosDe, formDe, formVazio, type FormCliente } from '../telas/clientes/ficha';
import { DocumentosCliente, EmailsCliente, PedidosSolicitacoesCliente, PortalCliente } from '../telas/clientes/portal';
import { DriveCliente } from '../telas/clientes/drive';
import { LinhaDoTempoCliente } from '../telas/clientes/linha-do-tempo';
import { FechamentoMes } from '../telas/agenda/fechamento';
import { RelatorioCliente } from '../telas/clientes/relatorio';
import { VinculosCliente } from '../telas/clientes/vinculos';
import { ParcelamentosCliente } from '../telas/clientes/parcelamentos';
import { Recorrencias } from '../telas/agenda/recorrentes';
import { Vencimentos } from '../telas/vencimentos/vencimentos';
import { Cofre } from '../telas/cofre/cofre';
import { FuncionariosCliente } from '../telas/clientes/funcionarios';
import { NotasDfe } from '../telas/clientes/notas-dfe';
import { IntegraCliente } from '../telas/integra/cliente';
import { CadastroRfbCliente } from '../telas/serpro/consultas';
import { ModalFichaEmpresa } from '../telas/gerador/gerador';

const ABAS = ['dados', 'regime', 'responsaveis', 'funcionarios', 'cofre', 'obrigacoes', 'fechamento', 'vencimentos', 'relatorio', 'historico', 'documentos', 'drive', 'portal', 'pedidos', 'emails', 'integra', 'notas'] as const;
type Aba = (typeof ABAS)[number];
/** A ficha em 5 grupos (cada um com suas abas): o que é cadastro, o que é trabalho, onde estão os arquivos, o que o cliente vê e a Receita. */
const GRUPOS = [
  { id: 'cadastro', rotulo: 'Cadastro', abas: ['dados', 'regime', 'responsaveis', 'funcionarios', 'cofre'] },
  { id: 'trabalho', rotulo: 'Trabalho', abas: ['obrigacoes', 'fechamento', 'vencimentos', 'relatorio', 'historico'] },
  { id: 'arquivos', rotulo: 'Arquivos', abas: ['documentos', 'drive'] },
  { id: 'portal', rotulo: 'Portal do cliente', abas: ['portal', 'pedidos', 'emails'] },
  { id: 'receita', rotulo: 'Receita e notas', abas: ['integra', 'notas'] },
] as const;
type Grupo = (typeof GRUPOS)[number]['id'];
const ROTULO_ABA: Record<Aba, string> = {
  dados: 'Dados', regime: 'Regime tributário', responsaveis: 'Responsáveis', funcionarios: 'Funcionários', cofre: 'Cofre de senhas',
  obrigacoes: 'Obrigações', fechamento: 'Fechamento do mês', vencimentos: 'Vencimentos', relatorio: 'Relatório mensal', historico: 'Linha do tempo',
  documentos: 'Documentos das tarefas', drive: 'Drive',
  portal: 'Contatos e acesso', pedidos: 'Pedidos e solicitações', emails: 'Envios e e-mails',
  integra: 'Receita Federal', notas: 'Notas (DF-e)',
};
function AbasDaFicha({ aba, onChange, c }: { aba: Aba; onChange: (a: Aba) => void; c: ClienteDetalhe }) {
  const grupo = GRUPOS.find((g) => (g.abas as readonly string[]).includes(aba)) ?? GRUPOS[0];
  return (
    <div className="abas-ficha">
      <Tabs<Grupo> valor={grupo.id} onChange={(g) => onChange(GRUPOS.find((x) => x.id === g)!.abas[0])} itens={GRUPOS.map((g) => ({ id: g.id, rotulo: g.rotulo }))} />
      <Tabs<Aba> valor={aba} onChange={onChange} itens={grupo.abas.map((x) => ({ id: x, rotulo: ROTULO_ABA[x], contador: x === 'regime' ? c.regimes.length : x === 'responsaveis' ? c.responsaveis.length : undefined }))} />
    </div>
  );
}
/** `aba` abre a ficha na aba certa; `enquete` abre a Enquete de obrigações (a empresa recém-cadastrada chega assim) */
export const Route = createFileRoute('/_app/clientes/$id')({ validateSearch: z.object({ aba: z.enum(ABAS).optional(), enquete: z.boolean().optional() }), component: ClientePagina });

function ClientePagina() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { eu } = useAuth();
  const papel = eu?.escritorio?.papel;
  const podeEditar = papel === 'ADMIN' || papel === 'SUPERVISOR' || papel === 'OPERADOR';
  const podeSupervisor = papel === 'ADMIN' || papel === 'SUPERVISOR';
  const busca = Route.useSearch();
  const [aba, setAba] = useState<Aba>(busca.aba ?? 'dados');

  const q = useQuery({ queryKey: ['cliente', id], queryFn: () => get<ClienteDetalhe>(`/v1/clientes/${id}`) });
  const [f, setF] = useState<FormCliente>(formVazio);
  const [tentou, setTentou] = useState(false);
  const [fichaAberta, setFichaAberta] = useState(false);
  useEffect(() => { if (q.data) setF(formDe(q.data)); }, [q.data]);
  const erros = tentou ? errosDe(f, false) : {};

  const salvar = useMutation({
    mutationFn: () => put<ClienteDetalhe>(`/v1/clientes/${id}`, bodyDe(f, false)),
    onSuccess: (c) => { qc.setQueryData(['cliente', id], c); void qc.invalidateQueries({ queryKey: ['clientes'] }); toast.ok('cliente salvo'); },
    onError: (e) => toast.erro(e),
  });

  if (q.isLoading) return <div className="conteudo"><div className="vazio">carregando…</div></div>;
  if (!q.data) return <div className="conteudo"><div className="alerta erro">cliente não encontrado</div></div>;
  const c = q.data;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>{c.razaoSocial}</h1>
          <div className="sub"><Link to="/clientes">clientes</Link> › {formatarDocumento(c.documento)}{c.codigo ? ` · cód. ${c.codigo}` : ''}{c.regimeAtual ? ` · ${ROTULO_REGIME[c.regimeAtual]}` : ''}{c.rfbSituacao && <> · RFB: <b style={{ color: /^(2|0) /.test(c.rfbSituacao) ? 'var(--green)' : 'var(--red)' }}>{c.rfbSituacao}</b></>}</div>
        </div>
        {aba === 'dados' && podeEditar && (
          <div className="acoes">
            <button className="btn" type="button" title="ficha cadastral em PDF, com o que está salvo: a versão para o cliente mandar a fornecedores, ou a do escritório" onClick={() => setFichaAberta(true)}>Ficha cadastral (PDF)…</button>
            <Link className="btn" to="/documentos" search={{ cliente: id }}>Gerar documento</Link>
            {papel === 'ADMIN' && <a className="btn" href={`/v1/clientes/${id}/exportacao`} title="todos os dados do cliente em JSON (LGPD / portabilidade)">↓ Exportar dados</a>}
            <button className="btn primario" type="button" disabled={salvar.isPending} onClick={() => { setTentou(true); if (Object.keys(errosDe(f, false)).length) { toast.erro('corrija os campos destacados'); return; } salvar.mutate(); }}>Salvar</button>
          </div>
        )}
      </div>
      <div className="conteudo">
        {fichaAberta && <ModalFichaEmpresa clienteId={id} podeGuardar={podeEditar} onFechar={() => setFichaAberta(false)} />}
        <AbasDaFicha aba={aba} onChange={setAba} c={c} />
        {aba === 'dados' && <FichaCliente f={f} set={(p) => setF((x) => ({ ...x, ...p }))} criando={false} erros={erros} clienteId={c.id} />}
        {aba === 'regime' && <Regimes c={c} podeEditar={podeEditar} podeRemover={podeSupervisor} />}
        {aba === 'responsaveis' && <Responsaveis c={c} podeEditar={podeSupervisor} />}
        {aba === 'funcionarios' && <FuncionariosCliente clienteId={c.id} />}
        {aba === 'obrigacoes' && <><VinculosCliente clienteId={c.id} abrirEnquete={!!busca.enquete} /><ParcelamentosCliente clienteId={c.id} /><Recorrencias clienteId={c.id} /></>}
        {aba === 'vencimentos' && <Vencimentos clienteId={c.id} />}
        {aba === 'cofre' && <Cofre clienteId={c.id} />}
        {aba === 'notas' && <NotasDfe clienteId={c.id} />}
        {aba === 'portal' && <PortalCliente clienteId={c.id} />}
        {aba === 'pedidos' && <PedidosSolicitacoesCliente clienteId={c.id} />}
        {aba === 'emails' && <EmailsCliente clienteId={c.id} />}
        {aba === 'documentos' && <DocumentosCliente clienteId={c.id} />}
        {aba === 'drive' && <DriveCliente clienteId={c.id} />}
        {aba === 'historico' && <LinhaDoTempoCliente clienteId={c.id} />}
        {aba === 'fechamento' && <FechamentoMes clienteId={c.id} />}
        {aba === 'integra' && <><Sec num={0} titulo="Cadastro na Receita Federal" sub={c.tipoPessoa === 'PJ' ? 'Consulta CNPJ: situação cadastral, CNAE, Simples/MEI, endereço e sócios' : 'Consulta CPF: situação cadastral (usa a data de nascimento da ficha)'}><CadastroRfbCliente clienteId={c.id} tipoPessoa={c.tipoPessoa} rfbSituacao={c.rfbSituacao} rfbConsultadoEm={c.rfbConsultadoEm} podeEditar={podeEditar} /></Sec><IntegraCliente clienteId={c.id} podeEditar={podeEditar} /></>}
        {aba === 'relatorio' && <RelatorioCliente clienteId={c.id} />}
      </div>
    </>
  );
}

function Regimes({ c, podeEditar, podeRemover }: { c: ClienteDetalhe; podeEditar: boolean; podeRemover: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [novo, setNovo] = useState<{ regime: RegimeTributario | ''; anexos: number[]; inicio: string; fim: string; obs: string } | null>(null);
  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['cliente', c.id] }); void qc.invalidateQueries({ queryKey: ['clientes'] }); };

  const add = useMutation({
    mutationFn: () => post(`/v1/clientes/${c.id}/regimes`, {
      regime: novo!.regime, anexoSimples: novo!.regime === 'SIMPLES' ? novo!.anexos[0] ?? null : null, anexosSimples: novo!.regime === 'SIMPLES' ? novo!.anexos : [],
      competenciaInicio: lerCompetencia(novo!.inicio), competenciaFim: novo!.fim ? lerCompetencia(novo!.fim) : null, observacao: novo!.obs || null,
    }),
    onSuccess: () => { invalidar(); setNovo(null); toast.ok('regime registrado'); },
    onError: (e) => toast.erro(e),
  });
  const remover = useMutation({
    mutationFn: (rid: string) => del(`/v1/clientes/${c.id}/regimes/${rid}`),
    onSuccess: () => { invalidar(); toast.ok('regime removido'); },
    onError: (e) => toast.erro(e),
  });
  const atual = competenciaAtual();

  return (
    <Sec titulo="Histórico de regime" sub="por faixa de competência — a competência anterior à mudança continua no regime antigo"
      acoes={podeEditar && <button className="btn mini primario" type="button" onClick={() => setNovo({ regime: '', anexos: [], inicio: formatarCompetencia(atual), fim: '', obs: '' })}>+ Mudança de regime</button>}>
      <div className="tbwrap">
        <table className="tb">
          <thead><tr><th>Regime</th><th>Anexos</th><th>De</th><th>Até</th><th>Obs.</th><th /></tr></thead>
          <tbody>
            {c.regimes.length === 0 && <tr><td className="vazio" colSpan={6}>nenhum regime registrado — sem ele o motor de prazos não sabe quais obrigações se aplicam</td></tr>}
            {c.regimes.map((r) => {
              const vigente = r.competenciaInicio <= atual && (r.competenciaFim == null || r.competenciaFim >= atual);
              return (
                <tr key={r.id}>
                  <td><b>{ROTULO_REGIME[r.regime]}</b> {vigente && <Pill cor="verde">vigente</Pill>}</td>
                  <td>{r.anexosSimples.length ? rotuloAnexos(r.anexosSimples) : '—'}</td>
                  <td className="mono">{formatarCompetencia(r.competenciaInicio)}</td>
                  <td className="mono">{r.competenciaFim ? formatarCompetencia(r.competenciaFim) : <span className="dim">em aberto</span>}</td>
                  <td className="dim">{r.observacao ?? ''}</td>
                  <td className="acoes">{podeRemover && <button className="btn mini perigo" type="button" onClick={() => { if (confirm('Remover esta faixa de regime?')) remover.mutate(r.id); }}>Remover</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal titulo="Mudança de regime" aberto={!!novo} onFechar={() => setNovo(null)} estreito
        acoes={<>
          <button className="btn" type="button" onClick={() => setNovo(null)}>Cancelar</button>
          <button className="btn primario" type="button" disabled={!novo?.regime || (novo.regime === 'SIMPLES' && !novo.anexos.length) || !lerCompetencia(novo.inicio) || add.isPending} onClick={() => add.mutate()}>Registrar</button>
        </>}>
        {novo && (
          <div className="fgrid">
            <Sel className="c12" rotulo="Novo regime" valor={novo.regime} vazio="—" opcoes={OPCOES_REGIME} onChange={(v) => setNovo({ ...novo, regime: v as RegimeTributario | '' })} autoFocus />
            {novo.regime === 'SIMPLES' && <CampoAnexos className="c12" obrigatorio valor={novo.anexos} onChange={(v) => setNovo({ ...novo, anexos: v })} />}
            <CampoCompetencia className="c6" rotulo="A partir da competência" valor={novo.inicio} onChange={(v) => setNovo({ ...novo, inicio: v })} placeholder="mm/aaaa" erro={lerCompetencia(novo.inicio) ? null : 'mm/aaaa'} ajuda="a faixa anterior é fechada automaticamente" />
            <CampoCompetencia className="c6" rotulo="Até (opcional)" valor={novo.fim} onChange={(v) => setNovo({ ...novo, fim: v })} placeholder="mm/aaaa" />
            <Campo className="c12" rotulo="Observação" valor={novo.obs} onChange={(v) => setNovo({ ...novo, obs: v })} />
          </div>
        )}
      </Modal>
    </Sec>
  );
}

function Responsaveis({ c, podeEditar }: { c: ClienteDetalhe; podeEditar: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const deps = useQuery({ queryKey: ['departamentos'], queryFn: () => get<Departamento[]>('/v1/departamentos') });
  const membros = useQuery({ queryKey: ['membros'], queryFn: () => get<Membro[]>('/v1/membros') });
  const [mapa, setMapa] = useState<Record<string, string>>({});
  useEffect(() => { setMapa(Object.fromEntries(c.responsaveis.map((r) => [r.departamentoId, r.membroId]))); }, [c.responsaveis]);

  const salvar = useMutation({
    mutationFn: () => put(`/v1/clientes/${c.id}/responsaveis`, Object.entries(mapa).filter(([, m]) => m).map(([departamentoId, membroId]) => ({ departamentoId, membroId }))),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cliente', c.id] }); toast.ok('responsáveis salvos'); },
    onError: (e) => toast.erro(e),
  });
  const ativos = (membros.data ?? []).filter((m) => m.ativo);

  return (
    <Sec titulo="Responsáveis por departamento" sub="quem recebe as tarefas deste cliente; pode ser sobrescrito por obrigação"
      acoes={podeEditar && <button className="btn mini primario" type="button" disabled={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</button>}>
      {deps.data?.length === 0 && <div className="alerta info">Cadastre departamentos em Equipe › Departamentos.</div>}
      <div className="fgrid">
        {(deps.data ?? []).filter((d) => d.ativo).map((d) => (
          <Sel key={d.id} className="c4" rotulo={`${d.sigla} · ${d.nome}`} valor={mapa[d.id] ?? ''} vazio="— ninguém —" disabled={!podeEditar}
            opcoes={ativos.filter((m) => !m.departamentoId || m.departamentoId === d.id || mapa[d.id] === m.id).map((m) => [m.id, m.apelido ? `${m.apelido} · ${m.nome}` : m.nome] as const)}
            onChange={(v) => setMapa({ ...mapa, [d.id]: v })} ajuda="lista quem é do departamento ou sem departamento" />
        ))}
      </div>
    </Sec>
  );
}
